import { Translate } from '@tn/foundation/hooks/providers/useTranslation';

const FROM_CONFIRMATION_QUERY_PARAM = 'fromConfirmation';
const MVPD_PROVIDER_QUERY_PARAM = 'mvpdProvider';
const AUTH_SUITE_TOKEN_STORAGE_KEY = 'auth-suite-applicationAccessToken';

/** Backend bind-failure code with dedicated legacy copy. */
export const REDEMPTION_CODE_ALREADY_USED = 'REDEMPTION_CODE_ALREADY_USED_BY_ANOTHER_USER';
/**
 * Structured backend errorCode for an invalid/undeserializable AuthSuite
 * token — arrives as an HTTP 400 {success, errorCode, message} body, mapped
 * back into extensions.code by MvpdAuthApi. Recovered from silently by
 * refreshing the token and re-binding; see mvpdBindActor's retry.
 */
export const AUTH_SUITE_TOKEN_INVALID = 'AUTH_SUITE_TOKEN_INVALID';
/**
 * Backend bind-failure message for an expired AuthSuite token — the exact
 * string cbs-core-ui matches as TOKEN_EXPIRED (ApiService constants), same
 * silent-refresh recovery as AUTH_SUITE_TOKEN_INVALID.
 */
export const AUTH_SUITE_TOKEN_EXPIRED = 'Unable to validate token reason: Token is not valid';

const TOKEN_INVALID_BIND_CODES: ReadonlySet<string> = new Set([
    AUTH_SUITE_TOKEN_INVALID,
    AUTH_SUITE_TOKEN_EXPIRED,
]);
/**
 * Backend bind-failure code for an account that already has access
 * (SUBSCRIBER/MVPD_AUTHZ). Legacy pre-checked this client-side from the
 * login response (emitAlreadySubscribedError); TN has no trustworthy
 * client-side status right after sign-in, so the backend rejection is
 * mapped to the same copy instead.
 */
export const ACTIVE_USER_PACKAGE_EXISTS = 'ACTIVE_USER_PACKAGE_EXISTS';

export type MvpdSignInHandoff = {
    authSuiteToken: string;
    providerName: string | null;
};

/**
 * True when this page load carries the legacy MVPD confirmation param
 * (?fromConfirmation=true). Deliberately looser than getMvpdSignInHandoff:
 * legacy SignIn.vue hides the TV-provider CTA on the param alone
 * (displayMVPDConnectCTA), whether or not an AuthSuite token survived.
 */
export const isFromMvpdConfirmation = (): boolean => {
    try {
        return (
            new URLSearchParams(window.location.search).get(FROM_CONFIRMATION_QUERY_PARAM) ===
            'true'
        );
    } catch {
        return false;
    }
};

/**
 * The legacy /partner (AuthSuite MVPD) confirmation screen sends anonymous
 * users to TN sign-in with ?fromConfirmation=true&mvpdProvider=<name>. The
 * AuthSuite application access token needed to bind the authenticated MVPD
 * to the account is persisted by the AuthSuite SDK in localStorage (same
 * origin as legacy); the provider display name only lives in the legacy
 * SPA's store, so it rides along as a query param. Token + flag present =
 * bind after login, the TN port of legacy SignIn.vue's registerWithApi.
 * Returns null when this sign-in is not an MVPD handoff.
 */
export const getMvpdSignInHandoff = (): MvpdSignInHandoff | null => {
    try {
        if (!isFromMvpdConfirmation()) {
            return null;
        }
        const params = new URLSearchParams(window.location.search);
        const authSuiteToken = window.localStorage.getItem(AUTH_SUITE_TOKEN_STORAGE_KEY);
        if (!authSuiteToken) {
            return null;
        }
        return { authSuiteToken, providerName: params.get(MVPD_PROVIDER_QUERY_PARAM) };
    } catch {
        return null;
    }
};

/**
 * The handoff only matters where the dtc machine will actually run the bind
 * (options.mvpdBindEnabled — P+ only). The single predicate for the dtc
 * guard, the sign-in submit actor's refresh skip and the loading-overlay
 * keep-up: on flag-off brands/regions a stray handoff must not divert the
 * normal sign-in completion.
 */
export const getEnabledMvpdSignInHandoff = (
    getConfigByKey: (key: string) => unknown,
): MvpdSignInHandoff | null =>
    getConfigByKey('options.mvpdBindEnabled') ? getMvpdSignInHandoff() : null;

/**
 * Legacy emitAlreadySubscribedError modal copy (title + {provider}
 * description), joined with an em dash for the sign-in form banner. Falls
 * back to the title alone when the handoff carried no provider name.
 */
export const getMvpdAlreadySubscribedMessage = (
    t: Translate['t'],
    providerName: string | null,
): string => {
    const title = t(
        'subscription_exists_title',
        null,
        'Your email address is already associated with a Paramount+ subscription.',
    );
    if (!providerName) {
        return title;
    }
    const description = t(
        'subscription_exists_description',
        { provider: providerName },
        `You won't be able to connect using your ${providerName} account until your preexisting Paramount+ subscription ends.`,
    );
    return `${title} — ${description}`;
};

type GraphQLRequestError = {
    response?: { errors?: { message?: string; extensions?: { code?: string } }[] };
};

/**
 * Backend failure code of a failed bindMvpd mutation. It travels in
 * extensions.code — GraphQL error MESSAGES are replaced by the
 * error-sanitization plugin in production, codes survive.
 */
export const getMvpdBindErrorCode = (error: unknown): string | undefined =>
    (error as GraphQLRequestError)?.response?.errors?.[0]?.extensions?.code;

export const isTokenInvalidBindError = (error: unknown): boolean => {
    const code = getMvpdBindErrorCode(error);
    return !!code && TOKEN_INVALID_BIND_CODES.has(code);
};

/**
 * Maps a failed bindMvpd mutation to banner copy. The backend failure code
 * travels in extensions.code — GraphQL error MESSAGES are replaced by the
 * error-sanitization plugin in production, codes survive.
 *
 * Legacy parity note: legacy has copy only for the redemption-code case
 * (SignIn.vue authSuiteTokenErrorCallBack). For every other failure legacy
 * is SILENT — registerWithApi ignores success:false, and TOKEN_EXPIRED only
 * triggers a hidden token-refresh retry. Surfacing a generic localized error
 * here instead of replicating that silence is deliberate.
 */
export const getMvpdBindErrorMessage = (
    error: unknown,
    t: Translate['t'],
    providerName: string | null,
): string => {
    const code = getMvpdBindErrorCode(error);
    if (code === ACTIVE_USER_PACKAGE_EXISTS) {
        return getMvpdAlreadySubscribedMessage(t, providerName);
    }
    if (code === REDEMPTION_CODE_ALREADY_USED) {
        const headline = t(
            'mvpd_connection_already_exists_headline',
            null,
            'Your voucher code has been redeemed and cannot be used as a method for login.',
        );
        const subheadline = t(
            'mvpd_connection_already_exists_subheadline',
            null,
            'Sign in with your Paramount+ login credentials. If you need assistance, please contact customer support.',
        );
        return `${headline} — ${subheadline}`;
    }
    return t(
        'an_error_has_occurred_please_try_again_at_a_later_time',
        null,
        'An error has occurred. Please try again at a later time.',
    );
};
